import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface SlideOverPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

// Right-hand drawer used to show device/patch insight without leaving the report page.
const SlideOverPanel: React.FC<SlideOverPanelProps> = ({ isOpen, onClose, title, subtitle, children }) => {
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);
  const rafRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      // Two rAFs: the first lets the browser paint the off-screen starting position,
      // the second flips the class so the transition actually has something to animate from.
      const raf1 = requestAnimationFrame(() => {
        const raf2 = requestAnimationFrame(() => setEntered(true));
        rafRef.current = raf2;
      });
      rafRef.current = raf1;
      return () => cancelAnimationFrame(rafRef.current);
    }

    setEntered(false);
    const timeout = setTimeout(() => setMounted(false), 300);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    if (!mounted) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mounted, onClose]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${entered ? 'opacity-60' : 'opacity-0'}`}
        onClick={onClose}
      />
      <div
        className={`absolute top-0 right-0 h-full w-full max-w-6xl bg-[#161617] border-l border-[#2a2a2a] shadow-2xl
          transition-transform duration-300 ease-out flex flex-col
          ${entered ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-start justify-between p-5 border-b border-[#2a2a2a] flex-shrink-0">
          <div>
            <p className="text-lg font-semibold text-white">{title}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="text-gray-400 hover:text-white p-1 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-auto">{children}</div>
      </div>
    </div>
  );
};

export default SlideOverPanel;
